/**
 * Persists paid/organic attribution params (gclid, UTMs) for the onboarding funnel.
 * Params arrive on the first app.getwemove.com URL from the marketing site CTAs.
 * Click IDs also live in a 90-day first-party cookie so they survive tab close.
 */

export const ATTRIBUTION_STORAGE_KEY = 'utm_params';

export const ATTRIBUTION_PARAM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'msclkid',
    'ref',
] as const;

export type AttributionParams = Partial<Record<(typeof ATTRIBUTION_PARAM_KEYS)[number], string>>;

const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const;
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

const cookieDomain = (): string => {
    if (typeof window === 'undefined') {
        return '';
    }
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
        return '';
    }
    if (hostname.endsWith('getwemove.com')) {
        return '; domain=.getwemove.com';
    }
    return '';
};

const readCookie = (cookieName: string): string | undefined => {
    if (typeof document === 'undefined') {
        return undefined;
    }
    const prefix = `${cookieName}=`;
    const match = document.cookie.split('; ').find((row) => row.startsWith(prefix));
    if (!match) {
        return undefined;
    }
    return decodeURIComponent(match.slice(prefix.length));
};

const writeClickIdCookie = (cookieName: string, cookieValue: string): void => {
    if (typeof document === 'undefined') {
        return;
    }
    document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${cookieDomain()}`;
};

const readClickIdCookies = (): AttributionParams => {
    const fromCookies: AttributionParams = {};
    CLICK_ID_KEYS.forEach((clickKey) => {
        const cookieValue = readCookie(clickKey);
        if (cookieValue) {
            fromCookies[clickKey] = cookieValue;
        }
    });
    return fromCookies;
};

const parseStoredParams = (): AttributionParams => {
    if (typeof window === 'undefined') {
        return {};
    }

    let fromSession: AttributionParams = {};
    try {
        const storedValue = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
        if (storedValue) {
            const parsedValue = JSON.parse(storedValue);
            if (typeof parsedValue === 'object' && parsedValue !== null) {
                fromSession = parsedValue as AttributionParams;
            }
        }
    } catch {
        fromSession = {};
    }

    return { ...readClickIdCookies(), ...fromSession };
};

/**
 * Merge URL attribution params into sessionStorage + click-id cookies (URL wins).
 * Call as early as possible on app.getwemove.com load.
 */
export const persistAttributionParams = (): AttributionParams => {
    if (typeof window === 'undefined') {
        return {};
    }

    const mergedParams: AttributionParams = { ...parseStoredParams() };
    const urlParams = new URLSearchParams(window.location.search);

    ATTRIBUTION_PARAM_KEYS.forEach((paramKey) => {
        const paramValue = urlParams.get(paramKey);
        if (paramValue) {
            mergedParams[paramKey] = paramValue;
        }
    });

    if (Object.keys(mergedParams).length > 0) {
        sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(mergedParams));
    }

    CLICK_ID_KEYS.forEach((clickKey) => {
        const clickValue = mergedParams[clickKey];
        if (clickValue) {
            writeClickIdCookie(clickKey, clickValue);
        }
    });

    return mergedParams;
};

export const getStoredAttributionParams = (): AttributionParams => parseStoredParams();

export const getClickAttributionForPayment = (): {
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
    fbclid?: string;
} => {
    const stored = getStoredAttributionParams();
    return {
        ...(stored.gclid ? { gclid: stored.gclid } : {}),
        ...(stored.gbraid ? { gbraid: stored.gbraid } : {}),
        ...(stored.wbraid ? { wbraid: stored.wbraid } : {}),
        ...(stored.fbclid ? { fbclid: stored.fbclid } : {}),
    };
};
